import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Globe,
  ExternalLink,
  Camera,
  AlertTriangle,
  Copy,
} from 'lucide-react';
import { cn, getDAColor, getSpamScoreColor } from '../lib/utils';
import StatusBadge from './StatusBadge';
import DomainTrustBadge from './DomainTrustBadge';

// Thumbnail with error fallback
function ScreenshotThumb({ src, onClick, size = 'sm' }) {
  const [hasError, setHasError] = useState(false);
  const dim = size === 'sm' ? 'w-8 h-8' : 'w-10 h-10';
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';

  if (!src || hasError) {
    return (
      <div className={cn(dim, 'rounded-md bg-muted/30 border border-border/30 flex items-center justify-center')}>
        <Camera className={cn(iconSize, 'text-muted-foreground/20')} />
      </div>
    );
  }
  return (
    <button
      onClick={onClick}
      className="inline-block rounded-md overflow-hidden border border-border/50 hover:border-primary/50 hover:shadow-sm transition-all"
    >
      <img
        src={src}
        alt=""
        className={cn(dim, 'object-cover object-top bg-muted')}
        loading="lazy"
        onError={() => setHasError(true)}
      />
    </button>
  );
}
import { checkCrossProjectDomains } from '../api/backlinks';
import { toast } from 'react-hot-toast';

const linkTypeBadge = {
  dofollow: { bg: 'bg-success/20', text: 'text-success', label: 'Follow' },
  nofollow: { bg: 'bg-warning/20', text: 'text-warning', label: 'Nofollow' },
  ugc: { bg: 'bg-info/20', text: 'text-info', label: 'UGC' },
  sponsored: { bg: 'bg-primary/20', text: 'text-primary', label: 'Sponsored' },
};

export default function DomainGroupView({
  groups = [],
  projectId,
  onBacklinkClick,
  onOpenLightbox,
  showDuplicatesOnly = false,
}) {
  const [expandedDomains, setExpandedDomains] = useState(new Set());
  const [crossProjectData, setCrossProjectData] = useState({});
  const [crossProjectLoading, setCrossProjectLoading] = useState(false);

  // Fetch cross-project data on mount
  useEffect(() => {
    if (groups.length === 0 || !projectId) return;

    const domains = groups.map(g => g.domain);
    setCrossProjectLoading(true);

    checkCrossProjectDomains(domains, projectId)
      .then(data => {
        setCrossProjectData(data.crossProject || {});
      })
      .catch(() => {
        // Silently fail - cross-project is a nice-to-have
      })
      .finally(() => {
        setCrossProjectLoading(false);
      });
  }, [groups, projectId]);

  const filteredGroups = useMemo(() => {
    if (showDuplicatesOnly) {
      return groups.filter(g => g.count >= 2);
    }
    return groups;
  }, [groups, showDuplicatesOnly]);

  // Summary stats for duplicates
  const duplicateStats = useMemo(() => {
    const dupGroups = groups.filter(g => g.count >= 2);
    const totalDupLinks = dupGroups.reduce((sum, g) => sum + g.count, 0);
    return { domains: dupGroups.length, links: totalDupLinks };
  }, [groups]);

  const toggleDomain = (domain) => {
    setExpandedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) {
        next.delete(domain);
      } else {
        next.add(domain);
      }
      return next;
    });
  };

  if (filteredGroups.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {showDuplicatesOnly
          ? 'No duplicate domains found. All domains have unique links.'
          : 'No backlinks found.'}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-muted/50 rounded-lg text-sm">
        <span className="text-muted-foreground">
          {showDuplicatesOnly ? (
            <>
              <span className="font-medium text-warning">{duplicateStats.domains}</span> domains with duplicate links
              {' '}(<span className="font-medium text-foreground">{duplicateStats.links}</span> total links)
            </>
          ) : (
            <>
              <span className="font-medium text-foreground">{filteredGroups.length}</span> domains
              {duplicateStats.domains > 0 && (
                <> &middot; <span className="text-warning font-medium">{duplicateStats.domains}</span> with duplicates</>
              )}
            </>
          )}
        </span>
        {Object.keys(crossProjectData).length > 0 && (
          <span className="text-xs text-purple-400">
            {Object.keys(crossProjectData).length} shared across projects
          </span>
        )}
      </div>

      {/* Domain groups */}
      {filteredGroups.map(group => {
        const isExpanded = expandedDomains.has(group.domain);
        const isDuplicate = group.count >= 2;
        const crossProject = crossProjectData[group.domain];

        return (
          <div
            key={group.domain}
            className={cn(
              'border rounded-lg overflow-hidden transition-colors',
              isDuplicate ? 'border-warning/30' : 'border-border',
            )}
          >
            {/* Domain header */}
            <button
              onClick={() => toggleDomain(group.domain)}
              className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                )}
                <Globe className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="font-medium text-foreground truncate">{group.domain}</span>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                {/* Domain Trust Badge (aggregated from first backlink's metrics) */}
                {group.backlinks[0] && (
                  <DomainTrustBadge
                    da={group.backlinks[0].domain_authority}
                    traffic={group.backlinks[0].traffic}
                    referringDomains={group.backlinks[0].dfs_referring_domains}
                    referringIps={group.backlinks[0].dfs_referring_ips}
                    backlinksCount={group.backlinks[0].dfs_backlinks_count}
                    spamScore={group.backlinks[0].spam_score}
                  />
                )}

                {/* Cross-project badge */}
                {crossProject && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400"
                    title={`Also in: ${crossProject.projects.map(p => p.name).join(', ')}`}
                  >
                    <Globe className="h-3 w-3" />
                    In {crossProject.count} other {crossProject.count === 1 ? 'project' : 'projects'}
                  </span>
                )}

                {/* Duplicate warning badge */}
                {isDuplicate && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning/20 text-warning">
                    <AlertTriangle className="h-3 w-3" />
                    {group.count}x
                  </span>
                )}

                {/* Link count */}
                <span className="px-2 py-0.5 bg-muted rounded text-sm text-foreground">
                  {group.count} {group.count === 1 ? 'link' : 'links'}
                </span>
              </div>
            </button>

            {/* Expanded backlinks table */}
            {isExpanded && (
              <div className="border-t border-border">
                <table className="w-full">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">URL</th>
                      <th className="w-10 px-2 py-2 text-center text-xs font-medium text-muted-foreground">
                        <Camera className="h-3 w-3 mx-auto" />
                      </th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Status</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Type</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">DA</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Anchor</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {group.backlinks.map(bl => (
                      <tr key={bl.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-2">
                          <Link
                            to={`/backlinks/${bl.id}`}
                            className="text-sm text-foreground hover:text-primary transition-colors block"
                          >
                            <span className="font-mono text-xs truncate block max-w-[300px]" title={bl.backlink_url}>
                              {(() => {
                                try {
                                  const u = new URL(bl.backlink_url);
                                  return u.pathname + u.search;
                                } catch {
                                  return bl.backlink_url;
                                }
                              })()}
                            </span>
                          </Link>
                          {bl.target_url && (
                            <span className="text-[11px] text-muted-foreground/60 truncate block max-w-[250px]" title={bl.target_url}>
                              &rarr; {(() => {
                                try {
                                  return new URL(bl.target_url).hostname.replace('www.', '');
                                } catch {
                                  return bl.target_url;
                                }
                              })()}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-center">
                          <ScreenshotThumb
                            src={bl.screenshot_url}
                            onClick={() => onOpenLightbox?.(bl.screenshot_url, bl.backlink_url)}
                            size="sm"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <StatusBadge status={bl.status} size="sm" />
                        </td>
                        <td className="px-4 py-2">
                          {bl.link_type ? (
                            <span className={cn(
                              'px-2 py-0.5 rounded text-xs font-medium',
                              linkTypeBadge[bl.link_type]?.bg || 'bg-secondary',
                              linkTypeBadge[bl.link_type]?.text || 'text-secondary-foreground'
                            )}>
                              {linkTypeBadge[bl.link_type]?.label || bl.link_type}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {bl.domain_authority ? (
                            <span className={cn('text-sm font-medium tabular-nums', getDAColor(bl.domain_authority))}>
                              {Math.round(bl.domain_authority)}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground/40">&mdash;</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {bl.anchor_text_found ? (
                            <div className="group/anchor flex items-center gap-1 max-w-[150px]">
                              <span className="text-success text-xs truncate" title={bl.anchor_text_found}>
                                {bl.anchor_text_found.length > 25
                                  ? bl.anchor_text_found.substring(0, 25) + '...'
                                  : bl.anchor_text_found}
                              </span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(bl.anchor_text_found);
                                  toast.success('Copied');
                                }}
                                className="flex-shrink-0 opacity-0 group-hover/anchor:opacity-100 p-0.5 text-muted-foreground hover:text-foreground transition-all"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">&mdash;</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <a
                            href={bl.backlink_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 text-muted-foreground hover:text-info transition-colors inline-block"
                            title="Open in new tab"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
